import React, {useEffect, useState} from "react";
import "./App.css";

const PRIZES_URL = `${process.env.PUBLIC_URL || ""}/prizes.json`;
const CONTENT_URL = `${process.env.PUBLIC_URL || ""}/content.json`;

const fallbackWinnerSection = {
  heading: "Du hast gewonnen?",
  textBeforeEmail:
    "Sehr schön! Dann schicke uns bitte bis zum 30.06.2025 eine E-Mail an ",
  email: "osterhasen@rt91.de",
  textAfterEmail:
    " mit einem Foto deiner Losnummer zu. Wir melden uns kurzfristig wegen der Gewinnübergabe.",
};

const hasValidPrizeShape = (prize) => {
  return (
    prize &&
    typeof prize === "object" &&
    Number.isInteger(prize.number) &&
    typeof prize.name === "string" &&
    prize.name.trim().length > 0
  );
};

const isValidPrizeList = (value) => {
  return Array.isArray(value) && value.every(hasValidPrizeShape);
};

const hasValidWinnerSectionShape = (winnerSection) => {
  return (
    winnerSection &&
    typeof winnerSection === "object" &&
    typeof winnerSection.heading === "string" &&
    winnerSection.heading.trim().length > 0 &&
    typeof winnerSection.textBeforeEmail === "string" &&
    typeof winnerSection.email === "string" &&
    winnerSection.email.trim().length > 0 &&
    typeof winnerSection.textAfterEmail === "string"
  );
};

const isValidContentConfig = (value) => {
  return (
    value &&
    typeof value === "object" &&
    hasValidWinnerSectionShape(value.winnerSection)
  );
};

const App = () => {
  const [searchNumber, setSearchNumber] = useState("");
  const [allPrizes, setAllPrizes] = useState([]);
  const [filteredPrizes, setFilteredPrizes] = useState([]);
  const [message, setMessage] = useState("");
  const [loadError, setLoadError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [winnerSection, setWinnerSection] = useState(fallbackWinnerSection);

  useEffect(() => {
    let isActive = true;

    const loadPrizes = async () => {
      try {
        const response = await fetch(PRIZES_URL, {cache: "no-store"});

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const nextPrizes = await response.json();

        if (!isValidPrizeList(nextPrizes)) {
          throw new Error("invalid prize list");
        }

        if (!isActive) {
          return;
        }

        setAllPrizes(nextPrizes);
        setLoadError("");
      } catch (error) {
        if (!isActive) {
          return;
        }

        setAllPrizes([]);
        setLoadError("Die aktuelle Preisliste konnte nicht geladen werden.");
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadPrizes();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    let isActive = true;

    const loadContentConfig = async () => {
      try {
        const response = await fetch(CONTENT_URL, {cache: "no-store"});

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const nextContent = await response.json();

        if (!isValidContentConfig(nextContent)) {
          throw new Error("invalid content config");
        }

        if (!isActive) {
          return;
        }

        setWinnerSection(nextContent.winnerSection);
      } catch (error) {
        if (!isActive) {
          return;
        }

        setWinnerSection(fallbackWinnerSection);
      }
    };

    loadContentConfig();

    return () => {
      isActive = false;
    };
  }, []);

  useEffect(() => {
    if (searchNumber === "") {
      setFilteredPrizes(allPrizes);
      setMessage("");
      return;
    }

    const filtered = allPrizes.filter((prize) =>
      prize.number.toString().includes(searchNumber),
    );

    if (filtered.length > 0) {
      setFilteredPrizes(filtered);
      setMessage("");
      return;
    }

    setFilteredPrizes([]);
    setMessage(`Losnummer ${searchNumber} hat leider nicht gewonnen.`);
  }, [allPrizes, searchNumber]);

  const handleInputChange = (e) => {
    const input = e.target.value.replace(/\D/g, "");
    setSearchNumber(input);
  };

  return (
    <div style={{padding: "20px", fontFamily: "Arial, sans-serif"}}>
      <h1>Osterhasenaktion - Preisübersicht</h1>
      <p className="text">
        Hier findest du die Übersicht mit allen Preisen & Gewinnnummern aus
        diesem Jahr. Gib deine Losnummer ein und prüfe schnell & einfach, ob du
        einen Preis gewonnen hast!
      </p>
      {loadError ? <p style={{color: "#8a5700"}}>{loadError}</p> : null}
      <input
        type="text"
        placeholder="Losnummer eingeben ..."
        value={searchNumber}
        onChange={handleInputChange}
        style={{padding: "10px", marginBottom: "20px", width: "300px"}}
      />
      {isLoading ? <p>Preisliste wird geladen ...</p> : null}
      {message && <p style={{color: "red"}}>{message}</p>}
      <ul>
        {filteredPrizes.map((prize, index) => (
          <li key={index}>
            <div className="prize-name">{prize.name}</div>
            <div className="prize-number">Losnummer: {prize.number}</div>
          </li>
        ))}
      </ul>
      {!message ? (
        <>
          <h2>{winnerSection.heading}</h2>
          <p className="text">
            {winnerSection.textBeforeEmail}
            <a href={`mailto:${winnerSection.email}`}>{winnerSection.email}</a>
            {winnerSection.textAfterEmail}
          </p>{" "}
        </>
      ) : undefined}
    </div>
  );
};

export default App;
